import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, Phone, MessageCircle, ShoppingCart, Settings } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/LanguageContext";
import { useAuth } from "@/App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_70076dd7-e4cb-484b-b629-fe23ba9127e6/artifacts/4hq9ct1g_LOGO.PNG";

const NAV_KEYS = [
  { href: "/", key: "home" },
  { href: "/houses", key: "houses" },
  { href: "/services", key: "services" },
  { href: "/menu", key: "menu" },
  { href: "/reviews", key: "reviews" },
  { href: "/contacts", key: "contacts" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isActive = (href) => location.pathname === href;
  const { lang, setLang, t } = useLanguage();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-gray-200 shadow-sm" data-testid="main-navbar">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src={LOGO_URL} alt="Eco Village" className="h-10 md:h-12" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-6">
            {NAV_KEYS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm font-sans tracking-wide transition-colors ${isActive(item.href) ? "text-primary" : "text-gray-600 hover:text-primary"}`}
                data-testid={`nav-${item.key}`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
            <Link to="/order" className="text-gray-500 hover:text-primary" data-testid="nav-order">
              <ShoppingCart className="w-5 h-5" />
            </Link>

            {/* Language toggle */}
            <button
              onClick={() => setLang(lang === "kz" ? "ru" : "kz")}
              className="text-xs font-semibold border border-primary px-2.5 py-1 text-primary hover:bg-primary/10 transition-colors tracking-widest"
              data-testid="lang-toggle"
            >
              {lang === "kz" ? "RU" : "KZ"}
            </button>

            {/* Admin button */}
            {user && (
              <Link to="/admin" data-testid="nav-admin-btn">
                <Settings className="w-4 h-4 text-gray-400 hover:text-primary transition-colors" />
              </Link>
            )}

            <Link to="/booking" data-testid="nav-booking-btn">
              <Button className="bg-primary text-primary-foreground font-medium px-6 py-2 rounded-none hover:bg-primary/90 uppercase tracking-widest text-xs btn-shine">
                {t("nav.booking")}
              </Button>
            </Link>
          </div>

          {/* Mobile nav */}
          <div className="lg:hidden flex items-center gap-2">
            <button onClick={() => setLang(lang === "kz" ? "ru" : "kz")} className="text-xs font-semibold border border-primary px-2 py-1 text-primary" data-testid="mobile-lang-toggle">
              {lang === "kz" ? "RU" : "KZ"}
            </button>
            <Link to="/booking" data-testid="mobile-booking-btn">
              <Button size="sm" className="bg-primary text-primary-foreground rounded-none text-xs uppercase tracking-widest px-3">
                {t("nav.booking")}
              </Button>
            </Link>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="text-[#2B2B2B] p-2" data-testid="mobile-menu-toggle">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-white/5 w-[300px]">
                <div className="flex flex-col gap-2 mt-8">
                  {NAV_KEYS.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setOpen(false)}
                      className={`font-serif text-2xl py-3 transition-colors ${isActive(item.href) ? "text-primary" : "text-foreground hover:text-primary"}`}
                      data-testid={`mobile-nav-${item.key}`}
                    >
                      {t(`nav.${item.key}`)}
                    </Link>
                  ))}
                  <Link to="/order" onClick={() => setOpen(false)} className="font-serif text-2xl py-3 text-foreground hover:text-primary">
                    {t("nav.order")}
                  </Link>
                  {user && (
                    <Link to="/admin" onClick={() => setOpen(false)} className="font-serif text-2xl py-3 text-primary">
                      {t("nav.admin")}
                    </Link>
                  )}
                  <div className="mt-8 flex flex-col gap-4">
                    <a href="tel:+77027033030" className="flex items-center gap-3 text-muted-foreground hover:text-foreground">
                      <Phone className="w-4 h-4" /> +7 702 703 30 30
                    </a>
                    <a href="https://wa.me/77027033030" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-muted-foreground hover:text-foreground">
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="pt-16 md:pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t bg-[#2B2B2B] text-white" data-testid="main-footer">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img src={LOGO_URL} alt="Eco Village" className="h-12 brightness-[2] mb-4" />
              <p className="text-white/60 text-sm leading-relaxed">{t("footer.desc")}</p>
            </div>
            <div>
              <h4 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 text-[#5EC75E]">{t("footer.navTitle")}</h4>
              <div className="flex flex-col gap-2">
                {NAV_KEYS.map((item) => (
                  <Link key={item.href} to={item.href} className="text-sm text-white/60 hover:text-white transition-colors">
                    {t(`nav.${item.key}`)}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 text-[#5EC75E]">{t("footer.contactTitle")}</h4>
              <div className="flex flex-col gap-3 text-sm text-white/60">
                <a href="tel:+77027033030" className="hover:text-white transition-colors">+7 702 703 30 30</a>
                <a href="https://wa.me/77027033030" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">WhatsApp</a>
                <a href="https://www.instagram.com/eco_village_kzo" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Instagram</a>
                <p>{t("contacts.addressText")}</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 flex items-center justify-between text-xs text-white/40">
            <span>&copy; {new Date().getFullYear()} Eco Village KZO. {t("footer.rights")}</span>
            <Link to="/admin" className="hover:text-[#5EC75E] transition-colors" data-testid="footer-admin-btn">
              {t("nav.admin")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
